import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { TypeCodeState } from '../../types';

const initialState = {
  bankCodes: null,
  clusterCodes: null,
  centerCodes: null,
  workPartCodes: null,
};

export type TypeBankCodeState = TypeCodeState & {
  logoImageUrl: string;
  type?: 'BANK' | 'SECURITY';
};

type TypeCodesStore = {
  /** 은행 코드 옵션 리스트 */
  bankCodes: TypeBankCodeState[] | null;
  /** 클러스터 코드 옵션 리스트 */
  clusterCodes: TypeCodeState[] | null;
  /** 센터 코드 옵션 리스트 */
  centerCodes: TypeCodeState[] | null;
  /** 업무 파트 코드 옵션 리스트 */
  workPartCodes: TypeCodeState[] | null;
  actions: {
    setBankCodes: (codes: TypeBankCodeState[] | null) => void;
    setClusterCodes: (codes: TypeCodeState[] | null) => void;
    setCenterCodes: (codes: TypeCodeState[] | null) => void;
    setWorkPartCodes: (codes: TypeCodeState[] | null) => void;
    resetCodes: () => void;
    getBankCodes: () => (Omit<TypeBankCodeState, 'code'> & { value: string })[];
    getBankCodesByType: (type: 'BANK' | 'SECURITY') => TypeBankCodeState[];
    getBankImageUrl: (code: string) => string | undefined;
  };
};

/** @description 상태를 로컬 스토리지에 저장하기 위한 키 */
const keyOfStorage = 'codes';

/** @description 옵션 리스트를 저장하는 상태 */
export const useCodesStore = create<TypeCodesStore>()(
  persist(
    (set, get) => ({
      ...initialState,
      actions: {
        setBankCodes: (codes) => set({ bankCodes: codes }),
        setClusterCodes: (codes) => set({ clusterCodes: codes }),
        setCenterCodes: (codes) => set({ centerCodes: codes }),
        setWorkPartCodes: (codes) => set({ workPartCodes: codes }),
        resetCodes: () => {
          set({ ...initialState });
          localStorage.removeItem(keyOfStorage);
        },
        getBankCodes: () => {
          return (
            get().bankCodes?.map((code) => ({
              value: code.code,
              text: code.text,
              logoImageUrl: code.logoImageUrl,
              type: code.type,
            })) ?? []
          );
        },
        getBankCodesByType: (type: 'BANK' | 'SECURITY') => {
          return get()?.bankCodes?.filter((code) => code.type === type) ?? [];
        },
        getBankImageUrl: (code: string) => {
          return get()?.bankCodes?.find((bank) => bank.code === code)?.logoImageUrl;
        },
      },
    }),
    {
      name: keyOfStorage,
      partialize: (state) => ({
        bankCodes: state.bankCodes,
        clusterCodes: state.clusterCodes,
        centerCodes: state.centerCodes,
        workPartCodes: state.workPartCodes,
      }),
    }
  )
);
